import { Button, CardProps, Center, Spinner, Stack, Text } from '@chakra-ui/react'
import { useBatchSkiptraceActions } from 'features/BatchSkiptrace/infra/views/BatchSkiptraceStore/hooks/useBatchSkiptraceActions'
import { useBatchSkiptraceAttemptState } from 'features/BatchSkiptrace/infra/views/BatchSkiptraceStore/hooks/useBatchSkiptraceAttemptState'
import { ConsumableAttemptBillingSummaryCard } from 'features/common/ConsumableAttempt/views/ConsumableAttemptBillingSummaryCard'
import { DateLib } from 'libs/Date'
import { Card } from 'presentation/components/molecules/Card'
import { LegacyBillingEvents } from 'presentation/screens/Billing/Billing.events'
import { BatchSkiptraceModalState, useBatchSkiptraceModalActions } from 'presentation/screens/ListScreen/components/BatchSkiptraceModal/useBatchSkiptraceModalStore'
import { useHasMoreSkiptracePlans } from 'presentation/screens/SkiptraceScreen/hooks/useHasMoreSkiptracePlans'
import { mbp } from 'presentation/utils/mapBreakpoint'
import { useEffect, useRef, useState } from 'react'
import { usePrevious } from 'react-use'

export type SkiptraceBreakdownStepCardProps = {
  payload: Extract<BatchSkiptraceModalState, { _tag: 'SkiptraceBreakdownStep' }>
} & CardProps

export const SkiptraceBreakdownStepCard = ({ payload, ...cardProps }: SkiptraceBreakdownStepCardProps) => {
  const modalActions = useBatchSkiptraceModalActions()
  const { hasMoreSkiptracePlans, loading: plansLoading } = useHasMoreSkiptracePlans()
  const actions = useBatchSkiptraceActions()
  const attemptState = useBatchSkiptraceAttemptState()
  const prevAttemptState = usePrevious(attemptState)

  // Extract data from the attempt
  const [attempt, setAttempt] = useState(payload.attempt)
  const availableFunds = attempt.funds.available
  const fundsStatus = attempt.status
  const pricePenniesPerUnit = attempt.price
  const refreshingConsumables = attempt.refreshingConsumables
  const remainingConsumables = attempt.remainingConsumables
  const nextRenewalDateRaw = attempt.consumablesRefreshDate
  const nextRenewalDate = DateLib.formatMMDDYYDots(nextRenewalDateRaw)
  const currentPlan = attempt.planName

  // Track if this is the initial render
  const didInitialSetup = useRef(false)

  // Track whether to show the payment confirmation
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false)

  // Calculate payment price based on the number of records to skiptrace
  const paymentPrice = pricePenniesPerUnit * payload.toSkiptraceCount

  // Handle proceeding to format step
  const handleNext = () => {
    // Just navigate to format step, passing the payload with attempt data
    // The confirmation will happen in the FormatStepCard
    modalActions.toFormatStep({
      ...payload,
      useFreeSkipsOnly: false,
      remainingConsumables,
      attempt,
    })
  }

  const handleUseFreeSkips = () => {
    modalActions.toFormatStep({
      ...payload,
      useFreeSkipsOnly: true,
      remainingConsumables,
      attempt,
    })
  }

  // Auto-proceed if user has sufficient consumables
  useEffect(() => {
    // Only run this once when all data is loaded and we haven't done setup yet
    if (!didInitialSetup.current && !plansLoading) {
      didInitialSetup.current = true

      if (fundsStatus === 'sufficient-consumables') {
        // Auto-confirm and skip confirmation if free skiptraces are available
        handleUseFreeSkips()
      }
    }
  }, [fundsStatus, plansLoading])

  // Update attempt state when funds are added
  useEffect(() => {
    const handleAddFundsDone = () => {
      actions.attemptBatchSkiptrace({
        ...payload,
        format: 'narrow', // Placeholder format
        useFreeSkipsOnly: false, // Assume false
        remainingConsumables: 0, // Because useFreeSkipsOnly is false, 0 is fine
      })
    }

    // Subscribe to the event and store the emitter
    const emitter = LegacyBillingEvents.addFundsDone.subscribe(handleAddFundsDone)

    // Clean up the subscription when component unmounts
    return () => {
      emitter.off('addFundsDone', handleAddFundsDone)
    }
  }, [])

  // Update the attempt state when re-attempting
  useEffect(() => {
    const didJustAttempt = prevAttemptState?._tag === 'Attempting'
      && attemptState._tag === 'Attempted'

    if (!didJustAttempt) return

    setAttempt(attemptState.attempt)
  }, [attemptState, prevAttemptState])

  // Show a loading spinner while we're checking if the user has plans with more skiptraces
  if (attemptState._tag === 'Attempting' || plansLoading)
    return <Center p={6}><Spinner /></Center>

  // If we have free skiptraces, this component shouldn't render (it would be auto-confirmed)
  // But just in case, return null
  if (fundsStatus === 'sufficient-consumables' && payload.toSkiptraceCount <= remainingConsumables)
    return null

  // Handler for proceeding to payment confirmation
  const handleProceedToPayment = () => {
    setShowPaymentConfirmation(true)
  }

  // Handle upgrading plan
  const handleUpgrade = () => {
    // Navigate to the pricing page with skiptraces section
    window.location.href = '/pricing#skiptraces'
    modalActions.close()
  }

  // Calculate the freeItemExceedCount - how many records exceed the free limit
  const calculateFreeItemExceedCount = () => {
    // If we have sufficient free consumables or the count is less than the free consumables, return 0
    if (fundsStatus === 'sufficient-consumables' && payload.toSkiptraceCount <= remainingConsumables)
      return 0

    // If we have some free consumables but not enough for all records, calculate the excess
    if (remainingConsumables > 0 && payload.toSkiptraceCount > remainingConsumables)
      return payload.toSkiptraceCount - remainingConsumables

    // If we have no free consumables, all records need to be paid for
    return payload.toSkiptraceCount
  }

  // Generate the appropriate subtitle based on whether we're using free skips or paying
  const getSubtitle = () => {
    const freeItemExceedCount = calculateFreeItemExceedCount()

    if (fundsStatus === 'sufficient-consumables') {
      if (freeItemExceedCount > 0)
        return `You're about to skiptrace ${payload.toSkiptraceCount} properties. ${remainingConsumables} will be covered by free skips. Do you want to pay for the remaining ${freeItemExceedCount}?`
      else
        return `You're about to skiptrace ${payload.toSkiptraceCount} properties. Do you want to use your free skips?`
    } else {
      return `You've run out of skips for this month. Do you want to pay for an additional ${payload.toSkiptraceCount} skips?`
    }
  }

  // For the payment confirmation modal
  if (showPaymentConfirmation) {
    return (
      <ConsumableAttemptBillingSummaryCard
        {...cardProps}
        maxW='736px'
        title='Run Skiptrace'
        subtitle={getSubtitle()}
        consumableType='skiptrace'
        paymentPrice={paymentPrice}
        freeItemExceedCount={calculateFreeItemExceedCount()}
        pricePenniesPerUnit={pricePenniesPerUnit}
        refreshingConsumables={refreshingConsumables}
        remainingConsumables={remainingConsumables}
        consumablesRefreshDate={nextRenewalDate}
        currentPlan={currentPlan}
        fundsStatus={fundsStatus}
        availableFunds={availableFunds}
        onAttempt={handleNext}
        onCancel={modalActions.close}
        onUseFreeSkips={calculateFreeItemExceedCount() > 0 && remainingConsumables > 0
          ? handleUseFreeSkips
          : undefined}
        quantity={calculateFreeItemExceedCount()}
      />
    )
  }

  // For the "out of free skiptraces" modal with upgrade options
  if (fundsStatus !== 'sufficient-consumables' && hasMoreSkiptracePlans) {
    return (
      <Card
        variant='default'
        colorScheme='modal.highlight'
        size={mbp({
          mobSm: 'xs-locked',
          mob: 'sm-locked',
          tabSm: 'lg-locked',
          dtLg: 'lg-locked',
        })}
        maxW='736px'
        {...cardProps}
      >
        <Text
          textStyle='h2'
          color='graystrong.500'
        >
          Out of Free Skiptraces
        </Text>

        <Text
          mt={mbp({ mobSm: 2, mob: 3, tabSm: 2, dtLg: 2 })}
          textStyle='bodyXLFat'
          color='graystrong.200'
        >
          You&apos;ve run out of free skiptraces for this month on your
          {' '}
          {currentPlan}
          {' '}
          plan.
        </Text>

        <Text
          mt={2}
          textStyle='bodyL'
          color='graystrong.200'
        >
          You can either pay for individual skiptraces or upgrade your plan for additional free skiptraces each month.
        </Text>

        <Stack
          justifyContent='center'
          gap={3}
          mt={mbp({ mobSm: 3, mob: 4, tabSm: 5 })}
          direction={{
            base: 'column',
            tabSm: 'row',
          }}
        >
          <Button
            variant='outline'
            colorScheme='neutral'
            size='lg'
            onClick={modalActions.close}
            order={{
              base: '3',
              tabSm: '1',
            }}
          >
            Cancel
          </Button>
          <Button
            variant='outline'
            colorScheme='positive'
            size='lg'
            onClick={handleProceedToPayment}
            order={{
              base: '2',
              tabSm: '2',
            }}
          >
            Pay for Skiptrace
          </Button>
          <Button
            w={mbp({ mobSm: 'auto', mob: 'auto' })}
            variant='solid'
            colorScheme='positive'
            size='lg'
            onClick={handleUpgrade}
            order={{
              base: '1',
              tabSm: '3',
            }}
          >
            Upgrade Plan
          </Button>
        </Stack>
      </Card>
    )
  }

  // Default case: standard billing card for payment-only option
  return (
    <ConsumableAttemptBillingSummaryCard
      {...cardProps}
      maxW='736px'
      title='Run Skiptrace'
      subtitle={getSubtitle()}
      consumableType='skiptrace'
      paymentPrice={paymentPrice}
      freeItemExceedCount={calculateFreeItemExceedCount()}
      pricePenniesPerUnit={pricePenniesPerUnit}
      refreshingConsumables={refreshingConsumables}
      remainingConsumables={remainingConsumables}
      consumablesRefreshDate={nextRenewalDate}
      currentPlan={currentPlan}
      fundsStatus={fundsStatus}
      availableFunds={availableFunds}
      onAttempt={handleNext}
      onCancel={modalActions.close}
      onUseFreeSkips={calculateFreeItemExceedCount() > 0 && remainingConsumables > 0
        ? handleUseFreeSkips
        : undefined}
      quantity={calculateFreeItemExceedCount()}
    />
  )
}
